import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { storiesApi } from '../../api/stories';
import './ConvertToSeriesModal.css'; // Reuse existing modal styles

/**
 * Modal for adding a new book (story) to an existing series. Reused as-is
 * by Start Book 1 (book-discovery-navigation unit), prefilled via
 * `initialTitle` — no separate "naming dialog" component.
 */
function AddBookModal({ isOpen, onClose, projectId, onCreated, initialTitle = '' }) {
  const [bookTitle, setBookTitle] = useState(initialTitle);
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setBookTitle(initialTitle);
      setDescription('');
      setError(null);
    }
  }, [isOpen, initialTitle]);

  const handleCreate = async () => {
    if (!bookTitle.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      // Create the story in the series
      const story = await storiesApi.create(projectId, {
        name: bookTitle.trim(),
        description: description.trim() || null
      });

      // Ensure the book's Discovery session exists (find-or-create — POST
      // /projects/{id}/stories already creates one for series projects, but
      // this is the single source of truth so re-entry and legacy books
      // stay covered too; a book never accumulates duplicates).
      const discovery = await storiesApi.ensureDiscoverySession(story.id);

      onCreated({ storyId: story.id, sessionId: discovery.session_id });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add book');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && bookTitle.trim() && !isLoading) {
      e.preventDefault();
      handleCreate();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Start a book</h2>

        {error && <div className="modal-error">{error}</div>}

        <div className="form-field">
          <label htmlFor="book-title">Book Title</label>
          <input
            id="book-title"
            type="text"
            value={bookTitle}
            onChange={e => setBookTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Book title"
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="form-field">
          <label htmlFor="book-description">Description (optional)</label>
          <textarea
            id="book-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Brief description..."
            disabled={isLoading}
            rows={3}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid var(--border-color, #ddd)',
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        <div className="modal-actions">
          <button
            className="button-secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="button-primary"
            onClick={handleCreate}
            disabled={!bookTitle.trim() || isLoading}
          >
            {isLoading ? 'Starting...' : 'Start a book'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AddBookModal;
